import React, { Component } from 'react'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import IconButton from '@material-ui/core/IconButton'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction'
import ListItemText from '@material-ui/core/ListItemText'
import Typography from '@material-ui/core/Typography'
import Tooltip from '@material-ui/core/Tooltip'
import Icon from '@mdi/react'
import { mdiDelete, mdiDownload, mdiRefresh } from '@mdi/js'
import {
  listStudyCatalog,
  listStudyCatalogOrBackfill,
  deleteLocalStudy,
  clearAllLocalStudies,
} from '../db/studyCatalog'
import { isDbAvailable } from '../db/dicomCache'
import { loadSession } from '../services/gatewayAuth'
import { loadControllerSession } from '../services/controllerAuth'
import { acceptStudyFromGateway } from '../services/gatewayStudies'
import { UDV_SURFACE, udvDialogProps } from './udvSurface'
import { t } from '../i18n'
import { getPwaInstallLabel } from '../utils/pwaInstallPrompt'

function formatBytes(n) {
  const b = Number(n) || 0
  if (b < 1024) return `${b} B`
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KiB`
  return `${(b / (1024 * 1024)).toFixed(1)} MiB`
}

function formatDate(dcm) {
  const s = String(dcm || '')
  if (s.length >= 8) {
    return `${s.slice(6, 8)}-${s.slice(4, 6)}-${s.slice(0, 4)}`
  }
  return s || '—'
}

class LocalStudiesDlg extends Component {
  constructor(props) {
    super(props)
    this.state = {
      studies: [],
      loading: false,
      busyUid: null,
      error: null,
      hasGateway: Boolean(loadSession()),
    }
    this._refreshGen = 0
  }

  componentDidMount() {
    if (this.props.open) this.refresh()
  }

  componentWillUnmount() {
    this._refreshGen += 1
  }

  componentDidUpdate(prevProps) {
    if (this.props.open && !prevProps.open) {
      this.setState({ hasGateway: Boolean(loadSession()), error: null })
      this.refresh()
    }
  }

  refresh = async () => {
    const gen = ++this._refreshGen
    this.setState({ loading: true, error: null })

    // Timeout de seguridad: si el IDB está bloqueado (otra pestaña con versión
    // anterior) las operaciones cuelgan indefinidamente. Después de 10 s
    // mostramos error en lugar de dejar el spinner para siempre.
    let safetyTimerId = null
    const safetyTimeout = new Promise((_, reject) => {
      safetyTimerId = setTimeout(
        () =>
          reject(
            new Error(
              'El storage local no respondió (IDB bloqueado). ' +
                'Cerrá otras pestañas de UDV y recargá.'
            )
          ),
        10000
      )
    })

    try {
      // 1) Pintar YA desde catálogo (sin parse DICOM) — no congelar UI
      let studies = await Promise.race([listStudyCatalog(), safetyTimeout])
      if (gen !== this._refreshGen) {
        clearTimeout(safetyTimerId)
        return
      }
      if (!studies.length) {
        studies = await Promise.race([
          listStudyCatalogOrBackfill({
            quick: true,
            maxEnrich: 0,
            scanUnlisted: false,
          }),
          safetyTimeout,
        ])
      }
      if (gen !== this._refreshGen) {
        clearTimeout(safetyTimerId)
        return
      }
      clearTimeout(safetyTimerId)
      this.setState({ studies, loading: false })

      // 2) Enrich acotado en background (cede event loop) — sin timeout extra
      const enriched = await listStudyCatalogOrBackfill({
        quick: false,
        maxEnrich: 4,
        scanUnlisted: true,
      })
      if (gen !== this._refreshGen) return
      this.setState({ studies: enriched })
    } catch (e) {
      clearTimeout(safetyTimerId)
      if (gen !== this._refreshGen) return
      this.setState({
        loading: false,
        error: (e && e.message) || String(e),
      })
    }
  }

  handleOpen = async (row) => {
    if (!row || !this.props.onOpenStudy) return
    this.setState({ busyUid: row.studyInstanceUID, error: null })
    if (this.props.onClose) this.props.onClose()
    try {
      await this.props.onOpenStudy(row.studyInstanceUID, row)
    } catch (e) {
      this.setState({ error: (e && e.message) || String(e) })
      if (this.props.onReopen) this.props.onReopen()
    } finally {
      this.setState({ busyUid: null })
    }
  }

  /**
   * Merge al Explorer (mismo paciente, estudio ya abierto).
   */
  handleLoad = async (row) => {
    if (!row || typeof this.props.onLoadStudy !== 'function') return
    this.setState({ busyUid: row.studyInstanceUID, error: null })
    if (this.props.onClose) this.props.onClose()
    try {
      await this.props.onLoadStudy(row.studyInstanceUID, row)
    } catch (e) {
      this.setState({ error: (e && e.message) || String(e) })
      if (this.props.onReopen) this.props.onReopen()
    } finally {
      this.setState({ busyUid: null })
    }
  }

  canShowLoad = (row) => {
    if (!row || typeof this.props.onLoadStudy !== 'function') return false
    if (typeof this.props.canLoadStudy === 'function') {
      return !!this.props.canLoadStudy(row)
    }
    return false
  }

  handleDelete = async (row) => {
    if (!row) return
    if (
      !window.confirm(
        `¿Eliminar del storage local el estudio de ${row.patientName || row.studyInstanceUID}?`
      )
    ) {
      return
    }
    this.setState({ busyUid: row.studyInstanceUID, error: null })
    try {
      await deleteLocalStudy(row.studyInstanceUID)
      await this.refresh()
    } catch (e) {
      this.setState({ error: (e && e.message) || String(e) })
    } finally {
      this.setState({ busyUid: null })
    }
  }

  handleClearAll = async () => {
    if (!window.confirm('¿Vaciar todo el storage local de estudios DICOM?')) return
    this.setState({ loading: true, error: null })
    try {
      await clearAllLocalStudies()
      await this.refresh()
    } catch (e) {
      this.setState({ error: (e && e.message) || String(e), loading: false })
    }
  }

  handleRedownload = async (row) => {
    if (!row) return
    const hasEdgeOrigin = !!(row.edgeApiBase || row.edgeWadoUrl)
    if (!hasEdgeOrigin && !loadSession()) {
      this.setState({
        error:
          'Inicie sesión en TJS Gateway o Controller para re-descargar.',
      })
      return
    }
    if (hasEdgeOrigin) {
      if (!loadControllerSession()) {
        this.setState({
          error: 'Inicie sesión en el Controller para re-descargar desde el Edge.',
        })
        return
      }
    }
    this.setState({ busyUid: row.studyInstanceUID, error: null })
    try {
      await acceptStudyFromGateway(row.studyInstanceUID, {
        replace: true,
        patientName: row.patientName,
        receivedInstances: row.gatewayReceivedInstances,
        ingestComplete: row.ingestComplete,
        edgeApiBase: row.edgeApiBase || undefined,
        edgeWadoUrl: row.edgeWadoUrl || undefined,
        edgeId: row.edgeId || undefined,
        clinicName: row.clinicName || undefined,
      })
      await this.refresh()
      if (this.props.onOpenStudy) {
        await this.props.onOpenStudy(row.studyInstanceUID, row)
        if (this.props.onClose) this.props.onClose()
      }
    } catch (e) {
      this.setState({ error: (e && e.message) || String(e) })
    } finally {
      this.setState({ busyUid: null })
    }
  }

  render() {
    const { open, onClose, canInstallPwa, onInstallPwa, offlineCacheDisabled } = this.props
    const { studies, loading, busyUid, error, hasGateway } = this.state
    const idbUnavailable = !isDbAvailable()

    return (
      <Dialog
        open={!!open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        {...udvDialogProps}
      >
        <DialogTitle style={UDV_SURFACE.title}>Estudios en storage local</DialogTitle>
        <DialogContent style={UDV_SURFACE.content}>
          {canInstallPwa && typeof onInstallPwa === 'function' ? (
            <div
              style={{
                marginBottom: 14,
                padding: '10px 12px',
                borderRadius: 8,
                border: '1px solid rgba(196, 165, 116, 0.35)',
                background: 'rgba(196, 165, 116, 0.08)',
              }}
            >
              <Typography variant="body2" style={{ marginBottom: 8, color: 'inherit' }}>
                {t('pwa.installIntro')}
              </Typography>
              <Button
                size="small"
                variant="contained"
                onClick={onInstallPwa}
                style={{
                  ...UDV_SURFACE.btnPrimary,
                  background: 'linear-gradient(180deg, #d4b896 0%, #c4a574 100%)',
                  color: '#1a1510',
                }}
              >
                {getPwaInstallLabel(t)}
              </Button>
            </div>
          ) : null}
          {loading && (
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
              <CircularProgress size={18} style={{ marginRight: 10 }} />
              <Typography variant="body2" style={UDV_SURFACE.muted}>
                Cargando catálogo…
              </Typography>
            </div>
          )}
          {error && (
            <Typography style={{ marginBottom: 12, color: '#ff8a80' }}>
              {error}
            </Typography>
          )}
          {offlineCacheDisabled && !loading && !error && (
            <div
              style={{
                marginBottom: 10,
                padding: '8px 12px',
                borderRadius: 6,
                border: '1px solid rgba(255,138,128,0.35)',
                background: 'rgba(255,138,128,0.08)',
              }}
            >
              <Typography variant="body2" style={{ color: '#ff8a80' }}>
                ⚠ El caché offline está deshabilitado para esta sesión. Suele venir del
                ticket de plataforma (<code>offline_cache: false</code>) o del override
                URL (<code>offlineCache=0</code>). Con esa política UDV no escribe IndexedDB
                ni catálogo local.
              </Typography>
            </div>
          )}
          {idbUnavailable && !loading && !error && (
            <div
              style={{
                marginBottom: 10,
                padding: '8px 12px',
                borderRadius: 6,
                border: '1px solid rgba(255,193,7,0.35)',
                background: 'rgba(255,193,7,0.06)',
              }}
            >
              <Typography variant="body2" style={{ color: '#ffd54f' }}>
                ⚠ Storage local no disponible: el índice de estudios está bloqueado por otra
                pestaña de UDV. Cerrá las pestañas duplicadas y recargá la página.
              </Typography>
            </div>
          )}
          {!loading && studies.length === 0 && !offlineCacheDisabled && !idbUnavailable && (
            <Typography variant="body2" style={UDV_SURFACE.muted}>
              No hay estudios en el storage local. Llegarán vía push del TJS Gateway
              o al cachear desde Orthanc.
            </Typography>
          )}
          {!loading && studies.length === 0 && offlineCacheDisabled && (
            <Typography variant="body2" style={UDV_SURFACE.muted}>
              Con caché offline deshabilitado no se almacenan estudios localmente.
              Pedí a plataforma/portal que el ticket traiga <code>offline_cache: true</code>
              (o quitá <code>offlineCache=0</code> de la URL) si querés Biblioteca local.
            </Typography>
          )}
          <List dense>
            {studies.map((row) => {
              const uid = row.studyInstanceUID
              const busy = busyUid === uid
              const dateLabel = formatDate(row.studyDate)
              const modalityLabel = row.modality || '—'
              const desc = (row.description || '').trim()
              const clinicLabel = (row.clinicName || '').trim()
              const secondaryParts = []
              if (clinicLabel) secondaryParts.push(clinicLabel)
              secondaryParts.push(
                `Fecha ${dateLabel}`,
                modalityLabel,
                `${row.instanceCount || 0} inst.`,
                formatBytes(row.bytesTotal),
                row.source || 'unknown'
              )
              const secondary = secondaryParts.join(' · ')
              return (
                <ListItem
                  button
                  key={uid}
                  disabled={busy}
                  onClick={() => this.handleOpen(row)}
                  style={UDV_SURFACE.listItem}
                >
                  <ListItemText
                    primary={
                      <span style={{ color: '#f0f4f8', fontWeight: 600 }}>
                        {row.patientName || 'Sin nombre'}
                      </span>
                    }
                    secondary={
                      <>
                        <Typography
                          component="span"
                          variant="body2"
                          style={{ color: 'rgba(200, 215, 230, 0.9)' }}
                        >
                          {secondary}
                        </Typography>
                        {desc ? (
                          <>
                            <br />
                            <Typography
                              component="span"
                              variant="body2"
                              style={{ color: 'rgba(180, 195, 210, 0.85)' }}
                            >
                              {desc}
                            </Typography>
                          </>
                        ) : null}
                        <br />
                        <Typography
                          component="span"
                          variant="caption"
                          style={{ color: 'rgba(140, 160, 180, 0.75)' }}
                        >
                          {uid}
                        </Typography>
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    {this.canShowLoad(row) ? (
                      <Button
                        size="small"
                        color="primary"
                        disabled={busy}
                        onClick={(e) => {
                          e.stopPropagation()
                          this.handleLoad(row)
                        }}
                        style={{
                          marginRight: 4,
                          textTransform: 'none',
                          color: '#90caf9',
                        }}
                      >
                        {t('relatedStudies.load')}
                      </Button>
                    ) : null}
                    <Tooltip title="Recargar en el visor">
                      <IconButton
                        edge="end"
                        disabled={busy}
                        onClick={(e) => {
                          e.stopPropagation()
                          this.handleOpen(row)
                        }}
                      >
                        {busy ? (
                          <CircularProgress size={18} />
                        ) : (
                          <Icon path={mdiRefresh} size={0.85} color="rgba(220,230,240,0.9)" />
                        )}
                      </IconButton>
                    </Tooltip>
                    {hasGateway && (
                      <Tooltip title="Re-descargar del Gateway">
                        <IconButton
                          edge="end"
                          disabled={!!busyUid}
                          onClick={(e) => {
                            e.stopPropagation()
                            this.handleRedownload(row)
                          }}
                        >
                          <Icon path={mdiDownload} size={0.85} color="rgba(220,230,240,0.9)" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Eliminar del storage">
                      <IconButton
                        edge="end"
                        disabled={!!busyUid}
                        onClick={(e) => {
                          e.stopPropagation()
                          this.handleDelete(row)
                        }}
                      >
                        <Icon path={mdiDelete} size={0.85} color="#ff8a80" />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              )
            })}
          </List>
        </DialogContent>
        <DialogActions style={UDV_SURFACE.actions}>
          <Button
            onClick={this.handleClearAll}
            style={UDV_SURFACE.btnDanger}
            disabled={loading || !!busyUid}
          >
            Vaciar todo
          </Button>
          <Button
            onClick={this.refresh}
            style={UDV_SURFACE.btnGhost}
            disabled={loading || !!busyUid}
          >
            Actualizar
          </Button>
          <Button onClick={onClose} style={UDV_SURFACE.btnPrimary} variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    )
  }
}

export default LocalStudiesDlg
